import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { type UseModalState } from "@src/lib/ui-react";
import {
  Children,
  createContext,
  isValidElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { SIDE_PANEL_TRANSITION, SidePanelSection } from "./SidePanelSection";

export { SIDE_PANEL_TRANSITION } from "./SidePanelSection";

const PANEL_TRIGGER_ATTRIBUTE = "data-panel-trigger";

/**
 * Spread onto any element whose clicks should not trigger the SidePanelGroup
 * click-outside-to-close behavior (e.g. a table row that updates panel content).
 * The element's own click handler is responsible for any resulting panel state
 * changes, including mutual exclusion across panels.
 */
export const PANEL_TRIGGER_PROPS = { [PANEL_TRIGGER_ATTRIBUTE]: true } as const;

type Anchor = "left" | "right";

/**
 * Returns the leading edge side -- the side of the panel group that faces the page content.
 */
function getLeadingEdgeSide(anchor: Anchor): Anchor {
  return anchor === "right" ? "left" : "right";
}

function toCssBorderSide(side: Anchor): "borderLeft" | "borderRight" {
  return side === "left" ? "borderLeft" : "borderRight";
}

/** A panel registered imperatively from deep inside the group's content rather than declared as a JSX child. */
interface RegisteredPanel {
  id: string;
  isOpen: boolean;
  width: number;
  closeOnOutsideClick: boolean;
  onClose: () => void;
  content: ReactNode;
}

interface SidePanelGroupContextValue {
  anchor: Anchor;
  registerPanel: (panel: RegisteredPanel) => void;
  unregisterPanel: (id: string) => void;
}

const SidePanelGroupContext = createContext<SidePanelGroupContextValue | null>(null);

export function useSidePanelGroup() {
  return useContext(SidePanelGroupContext);
}

interface UseRegisteredSidePanelParams {
  /** Stable identifier, unique within the group. */
  id: string;
  isOpen: boolean;
  width?: number;
  closeOnOutsideClick?: boolean;
  onClose: () => void;
  content: ReactNode;
}

/**
 * Registers a panel into the nearest `SidePanelGroup` as its outermost
 * (leading-edge) sibling. Returns `isInGroup: false` when there is no
 * surrounding group, letting the caller fall back to another surface.
 *
 * Pass stable (memoized) `content`/`onClose` so the data-sync effect doesn't
 * re-register the panel every render.
 */
export function useRegisteredSidePanel(params: UseRegisteredSidePanelParams): {
  isInGroup: boolean;
} {
  const { id, isOpen, width = 300, closeOnOutsideClick = true, onClose, content } = params;
  const group = useSidePanelGroup();

  // First effect syncs panel data in place; second unregisters only on unmount so updates don't reorder siblings.
  useEffect(() => {
    if (isDefined(group)) {
      group.registerPanel({ id, isOpen, width, closeOnOutsideClick, onClose, content });
    }
  }, [group, id, isOpen, width, closeOnOutsideClick, onClose, content]);

  useEffect(() => {
    if (!isDefined(group)) {
      return undefined;
    }

    return () => {
      group.unregisterPanel(id);
    };
  }, [group, id]);

  return { isInGroup: isDefined(group) };
}

interface SidePanelChildProps {
  modalState: UseModalState;
  width?: number;
  closeOnOutsideClick?: boolean;
}

interface SidePanelGroupProps {
  /**
   * Which side the panel group anchors to
   * @default "right"
   */
  anchor?: Anchor;
  /**
   * SidePanel children - order in JSX = order in layout (first = innermost)
   */
  children: ReactNode;
}

/**
 * Container for multiple SidePanel components that expand/collapse together.
 *
 * - Panels stack based on JSX order (first child = innermost panel)
 * - For anchor="right": panels expand leftward
 * - For anchor="left": panels expand rightward
 * - Total width animates smoothly as panels open/close
 * - Clicking outside while any panel is open closes every open panel. To opt
 *   a trigger element out of this (e.g. a table row whose click updates panel
 *   content rather than closing), spread `PANEL_TRIGGER_PROPS` onto that
 *   element or any ancestor. Its click handler then owns resulting panel state.
 *   A panel can opt itself out entirely with `closeOnOutsideClick={false}`.
 * - Panels registered via `useRegisteredSidePanel` render as the outermost
 *   (leading-edge) sibling and participate in width, ordering, and
 *   click-outside just like declared children.
 *
 * @example
 * ```tsx
 * // First child = innermost (rightmost for right anchor)
 * // Second child = expands leftward
 * const detailsState = useModalState();
 * const logState = useModalState();
 *
 * <SidePanelGroup anchor="right">
 *   <SidePanel modalState={detailsState} width={300}>
 *     <ShiftDetails />
 *   </SidePanel>
 *   <SidePanel modalState={logState} width={300}>
 *     <ShiftLog />
 *   </SidePanel>
 * </SidePanelGroup>
 * ```
 */
export function SidePanelGroup({ anchor = "right", children }: SidePanelGroupProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pendingCloseRef = useRef<number | null>(null);

  const [registeredPanels, setRegisteredPanels] = useState<RegisteredPanel[]>([]);

  const registerPanel = useCallback((panel: RegisteredPanel) => {
    setRegisteredPanels((previous) =>
      previous.some((existing) => existing.id === panel.id)
        ? previous.map((existing) => (existing.id === panel.id ? panel : existing))
        : [...previous, panel]
    );
  }, []);

  const unregisterPanel = useCallback((id: string) => {
    setRegisteredPanels((previous) => previous.filter((existing) => existing.id !== id));
  }, []);

  // Keep a live ref so the mousedown handler always reads current children
  // without needing to be recreated on every render.
  const childrenRef = useRef<ReactNode>(null);
  childrenRef.current = children;

  const registeredPanelsRef = useRef<RegisteredPanel[]>(registeredPanels);
  registeredPanelsRef.current = registeredPanels;

  // Calculate total width from open children and open registered panels
  const { totalWidth, hasAnyOpen, openCount } = useMemo(() => {
    let width = 0;
    let count = 0;

    Children.forEach(children, (child) => {
      const props = getSidePanelProps(child);
      if (isDefined(props) && props.modalState.modalIsOpen) {
        width += props.width ?? 300;
        count += 1;
      }
    });

    for (const panel of registeredPanels) {
      if (panel.isOpen) {
        width += panel.width;
        count += 1;
      }
    }

    return { totalWidth: width, hasAnyOpen: count > 0, openCount: count };
  }, [children, registeredPanels]);

  useEffect(() => {
    if (!hasAnyOpen) {
      return;
    }

    function handleMouseDown(event: MouseEvent) {
      const { target } = event;
      if (!(target instanceof Node)) {
        return;
      }
      // Don't close when clicking inside a portal-rendered overlay (MUI Dialog, Popover,
      // Tooltip, etc.) or a notistack toast. These render outside containerRef in the DOM but
      // are logically part of the panel's UI -- e.g. dismissing an error toast spawned by a
      // panel mutation should not also close every open panel.
      if (
        target instanceof Element &&
        target.closest(
          ".MuiModal-root, .MuiPopover-root, .MuiAutocomplete-popper, .MuiTooltip-popper, .notistack-SnackbarContainer"
        )
      ) {
        return;
      }
      // Don't close when clicking an element marked as a panel trigger (e.g. table rows that
      // update panel content). The trigger's own click handler manages mutual exclusion.
      if (target instanceof Element && target.closest(`[${PANEL_TRIGGER_ATTRIBUTE}]`)) {
        return;
      }
      if (containerRef.current?.contains(target)) {
        return;
      }

      // Snapshot open panels by modalState object reference at mousedown time.
      // At rAF we re-check live references: if the parent re-rendered (e.g. a
      // row-click updating content), its modalState object will have changed —
      // different reference means we leave that panel alone.
      const snapshot: UseModalState[] = [];
      Children.forEach(childrenRef.current, (child) => {
        const props = getSidePanelProps(child);
        if (
          isDefined(props) &&
          props.modalState.modalIsOpen &&
          props.closeOnOutsideClick !== false
        ) {
          snapshot.push(props.modalState);
        }
      });

      const registeredSnapshot: string[] = [];
      for (const panel of registeredPanelsRef.current) {
        if (panel.isOpen && panel.closeOnOutsideClick !== false) {
          registeredSnapshot.push(panel.id);
        }
      }

      if (snapshot.length === 0 && registeredSnapshot.length === 0) {
        return;
      }

      if (isDefined(pendingCloseRef.current)) {
        cancelAnimationFrame(pendingCloseRef.current);
      }
      pendingCloseRef.current = requestAnimationFrame(() => {
        pendingCloseRef.current = null;

        const liveStates = new Set<UseModalState>();
        Children.forEach(childrenRef.current, (child) => {
          const props = getSidePanelProps(child);
          if (isDefined(props)) {
            liveStates.add(props.modalState);
          }
        });

        for (const modalState of snapshot) {
          if (liveStates.has(modalState) && modalState.modalIsOpen) {
            modalState.closeModal();
          }
        }

        for (const id of registeredSnapshot) {
          const live = registeredPanelsRef.current.find((panel) => panel.id === id);
          if (isDefined(live) && live.isOpen) {
            live.onClose();
          }
        }
      });
    }

    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      if (isDefined(pendingCloseRef.current)) {
        cancelAnimationFrame(pendingCloseRef.current);
        pendingCloseRef.current = null;
      }
    };
  }, [hasAnyOpen]);

  const contextValue = useMemo(
    () => ({ anchor, registerPanel, unregisterPanel }),
    [anchor, registerPanel, unregisterPanel]
  );

  // For right anchor, reverse children so first child appears rightmost (innermost)
  const orderedChildren = useMemo(() => {
    const childArray = Children.toArray(children);
    return anchor === "right" ? childArray.toReversed() : childArray;
  }, [children, anchor]);

  // Rendered on the leading edge (outermost, facing the page content): before
  // declared children for a right anchor, after them for a left anchor.
  const registeredElements = registeredPanels.map((panel) => (
    <SidePanelSection
      key={`registered-${panel.id}`}
      anchor={anchor}
      isOpen={panel.isOpen}
      width={panel.width}
    >
      {panel.content}
    </SidePanelSection>
  ));

  return (
    <SidePanelGroupContext.Provider value={contextValue}>
      <Box
        ref={containerRef}
        sx={(theme) => {
          const leadingEdge = getLeadingEdgeSide(anchor);
          const border = `${theme.borderWidth?.regular} solid ${theme.palette.border?.base}`;

          return {
            position: "absolute",
            top: 0,
            bottom: 0,
            [anchor]: 0,
            width: totalWidth,
            transition: `width ${SIDE_PANEL_TRANSITION}, transform ${SIDE_PANEL_TRANSITION}`,
            transform: hasAnyOpen
              ? "translateX(0)"
              : `translateX(${anchor === "right" ? "100%" : "-100%"})`,
            backgroundColor: theme.palette.background.surface,
            [toCssBorderSide(leadingEdge)]: border,
            boxShadow: theme.shadow?.drawer,
            // Match the page container's bottom corner so no background leaks through the clip.
            [`borderBottom${anchor === "right" ? "Right" : "Left"}Radius`]:
              theme.borderRadius?.xLarge,
            // Round the leading edge only when multiple panels are expanded;
            // a single panel sits flush against the page content.
            ...(openCount > 1 && {
              [`borderTop${leadingEdge}Radius`]: theme.borderRadius?.xLarge,
              [`borderBottom${leadingEdge}Radius`]: theme.borderRadius?.xLarge,
            }),
            zIndex: theme.zIndex.drawer,
            display: "flex",
            flexDirection: "row",
            overflow: "hidden",
            willChange: "transform, width",
            backfaceVisibility: "hidden",
          };
        }}
      >
        {anchor === "right" ? (
          <>
            {registeredElements}
            {orderedChildren}
          </>
        ) : (
          <>
            {orderedChildren}
            {registeredElements}
          </>
        )}
      </Box>
    </SidePanelGroupContext.Provider>
  );
}

function getSidePanelProps(child: ReactNode): SidePanelChildProps | undefined {
  if (
    isValidElement(child) &&
    typeof child.type === "function" &&
    (child.type as { displayName?: string }).displayName === "SidePanel"
  ) {
    return child.props as SidePanelChildProps;
  }
  return undefined;
}
